import { ReactNode, useEffect, useRef, useState } from "react";

/** Expand/collapse duration in ms; keep in sync with the `duration-300` class. */
const TRANSITION_MS = 500;

/**
 * Props for the animated collapse wrapper.
 */
interface CollapsibleSectionProps {
  /** Whether the content is visible. */
  expanded: boolean;
  /** Content to reveal. Always mounted so height can be measured. */
  children: ReactNode;
  /** Extra classes applied to the inner content wrapper. */
  className?: string;
}

/**
 * Animates height and opacity when opening or closing an accordion body.
 *
 * Height is measured from the content rather than capped with a fixed
 * `max-height`, so families with many members are never clipped. Once fully
 * open the inline height is dropped so nested sections can grow freely.
 *
 * @param props - Expanded flag and content
 * @returns Wrapper that transitions its content in and out
 */
const CollapsibleSection = ({
  expanded,
  children,
  className = "",
}: CollapsibleSectionProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  // null means "auto": fully open, so nested content can resize without clipping.
  const [height, setHeight] = useState<number | null>(expanded ? null : 0);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    if (expanded) {
      setHeight(element.scrollHeight);
      const timer = setTimeout(() => setHeight(null), TRANSITION_MS);
      return () => clearTimeout(timer);
    }

    // Collapsing from "auto" needs a concrete start height before animating to 0.
    setHeight(element.scrollHeight);
    const frame = requestAnimationFrame(() => setHeight(0));
    return () => cancelAnimationFrame(frame);
  }, [expanded]);

  const isAnimating = height !== null;

  return (
    <div
      className={`transition-[height,opacity] duration-300 ease-in-out ${
        isAnimating ? "overflow-hidden" : ""
      } ${expanded ? "opacity-100" : "opacity-0"}`}
      style={isAnimating ? { height } : undefined}
      aria-hidden={!expanded}
    >
      <div ref={contentRef} className={className}>
        {children}
      </div>
    </div>
  );
};

export default CollapsibleSection;
